// Google Drive Backup Service
// Scope: drive.file (nur Dateien die diese App erstellt hat)
// Tokens in localStorage (kein medizinisches Datum, drive.file-Scope begrenzt)

import { exportDexieEncrypted } from './backupService';
import { loadSetting, saveSetting } from './settingsService';

const DRIVE_SCOPE    = 'https://www.googleapis.com/auth/drive.file';
const REDIRECT_URI   = (): string => window.location.origin + '/auth/google/callback';
const FOLDER_NAME    = 'AMed App';
const MAX_BACKUPS    = 7;
const BACKUP_INTERVAL_H = 24;

// localStorage keys (kurzlebige OAuth-Tokens, kein med. Datum)
const LS_REFRESH        = 'amed_drive_refresh';
const LS_CLIENT_ID      = 'amed_drive_clientid';
const LS_CLIENT_SECRET  = 'amed_drive_clientsecret';
const LS_ACCESS         = 'amed_drive_access';
const LS_ACCESS_EXP     = 'amed_drive_access_exp';

// Dexie keys
const KEY_FOLDER_ID     = 'drive_folder_id';
const KEY_LAST_BACKUP   = 'drive_last_backup';

// ── PKCE Helpers ──────────────────────────────────────────────────────────

function base64url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return base64url(arr.buffer);
}

async function generateChallenge(verifier: string): Promise<string> {
  return base64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
}

// ── OAuth-Flow ────────────────────────────────────────────────────────────

export async function startDriveOAuth(clientId: string, clientSecret: string): Promise<void> {
  const verifier  = generateVerifier();
  const challenge = await generateChallenge(verifier);
  const state     = base64url(crypto.getRandomValues(new Uint8Array(16)).buffer);

  localStorage.setItem(LS_CLIENT_ID,           clientId);
  localStorage.setItem(LS_CLIENT_SECRET,       clientSecret);
  localStorage.setItem('amed_drive_verifier',  verifier);
  localStorage.setItem('amed_drive_state',     state);
  localStorage.setItem('amed_drive_pending',   'true');

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             clientId,
    redirect_uri:          REDIRECT_URI(),
    scope:                 DRIVE_SCOPE,
    access_type:           'offline',
    prompt:                'consent',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export async function handleDriveOAuthCallback(
  code: string,
  state: string,
): Promise<boolean> {
  const savedState   = localStorage.getItem('amed_drive_state');
  const codeVerifier = localStorage.getItem('amed_drive_verifier');
  const clientId     = localStorage.getItem(LS_CLIENT_ID);
  const clientSecret = localStorage.getItem(LS_CLIENT_SECRET);
  const redirectUri  = REDIRECT_URI();

  localStorage.removeItem('amed_drive_state');
  localStorage.removeItem('amed_drive_verifier');
  localStorage.removeItem('amed_drive_pending');

  if (state !== savedState || codeVerifier === null || clientId === null || clientSecret === null) {
    return false;
  }

  try {
    const resp = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        code,
        client_id:     clientId,
        client_secret: clientSecret,
        redirect_uri:  redirectUri,
        grant_type:    'authorization_code',
        code_verifier: codeVerifier,
      }),
    });

    if (!resp.ok) return false;

    const data = await resp.json() as {
      access_token:    string;
      refresh_token?:  string;
      expires_in:      number;
    };

    localStorage.setItem(LS_ACCESS,     data.access_token);
    localStorage.setItem(LS_ACCESS_EXP, String(Date.now() + data.expires_in * 1000));

    if (data.refresh_token !== undefined && data.refresh_token !== '') {
      localStorage.setItem(LS_REFRESH, data.refresh_token);
    }

    return true;
  } catch {
    return false;
  }
}

// ── Status ────────────────────────────────────────────────────────────────

export function isDriveConnected(): boolean {
  return localStorage.getItem(LS_REFRESH) !== null;
}

export function disconnectDrive(): void {
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem(LS_CLIENT_ID);
  localStorage.removeItem(LS_CLIENT_SECRET);
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_ACCESS_EXP);
  void saveSetting(KEY_FOLDER_ID, null);
}

export async function getLastBackupTime(): Promise<string | null> {
  return loadSetting<string | null>(KEY_LAST_BACKUP, null);
}

// ── Token-Management ──────────────────────────────────────────────────────

async function getAccessToken(): Promise<string | null> {
  const token = localStorage.getItem(LS_ACCESS);
  const exp   = Number(localStorage.getItem(LS_ACCESS_EXP) ?? '0');

  if (token !== null && exp > Date.now() + 60_000) return token;

  const refreshToken = localStorage.getItem(LS_REFRESH);
  const clientId     = localStorage.getItem(LS_CLIENT_ID);
  const clientSecret = localStorage.getItem(LS_CLIENT_SECRET);
  if (refreshToken === null || clientId === null || clientSecret === null) return null;

  const resp = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      refresh_token:  refreshToken,
      client_id:      clientId,
      client_secret:  clientSecret,
      grant_type:     'refresh_token',
    }),
  });

  if (!resp.ok) return null;

  const data = await resp.json() as { access_token: string; expires_in: number };
  localStorage.setItem(LS_ACCESS,     data.access_token);
  localStorage.setItem(LS_ACCESS_EXP, String(Date.now() + data.expires_in * 1000));
  return data.access_token;
}

// ── Drive API ─────────────────────────────────────────────────────────────

async function driveApi(path: string, options: RequestInit, token: string): Promise<Response> {
  return fetch(`https://www.googleapis.com/drive/v3${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string> | undefined ?? {}),
    },
  });
}

async function getOrCreateFolder(token: string): Promise<string> {
  const cached = await loadSetting<string | null>(KEY_FOLDER_ID, null);
  if (cached !== null) {
    const check = await driveApi(`/files/${cached}?fields=id,trashed`, {}, token);
    if (check.ok) {
      const f = await check.json() as { id: string; trashed: boolean };
      if (!f.trashed) return f.id;
    }
  }

  const q = encodeURIComponent(`name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`);
  const list = await driveApi(`/files?q=${q}&fields=files(id)`, {}, token);
  if (list.ok) {
    const data = await list.json() as { files: Array<{ id: string }> };
    if (data.files.length > 0) {
      const id = data.files[0]!.id;
      await saveSetting(KEY_FOLDER_ID, id);
      return id;
    }
  }

  const create = await driveApi('/files', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
  }, token);

  const folder = await create.json() as { id: string };
  await saveSetting(KEY_FOLDER_ID, folder.id);
  return folder.id;
}

async function rotateOldBackups(folderId: string, token: string): Promise<void> {
  const q    = encodeURIComponent(`'${folderId}' in parents and name contains '.amedBackup' and trashed=false`);
  const resp = await driveApi(`/files?q=${q}&orderBy=createdTime&fields=files(id)`, {}, token);
  if (!resp.ok) return;

  const data     = await resp.json() as { files: Array<{ id: string }> };
  const toDelete = data.files.slice(0, Math.max(0, data.files.length - MAX_BACKUPS + 1));
  for (const f of toDelete) {
    await driveApi(`/files/${f.id}`, { method: 'DELETE' }, token);
  }
}

// ── Öffentliche Backup-Funktionen ─────────────────────────────────────────

export async function uploadDriveBackup(): Promise<void> {
  const token = await getAccessToken();
  if (token === null) throw new Error('Nicht mit Google Drive verbunden.');

  const backupFile = await exportDexieEncrypted();
  const ts         = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
  const fileName   = `amed-backup-${ts}.amedBackup`;
  const content    = JSON.stringify(backupFile);
  const folderId   = await getOrCreateFolder(token);

  await rotateOldBackups(folderId, token);

  const metadata = JSON.stringify({ name: fileName, parents: [folderId] });
  const body     = new FormData();
  body.append('metadata', new Blob([metadata], { type: 'application/json' }));
  body.append('file',     new Blob([content],  { type: 'application/octet-stream' }), fileName);

  const upload = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
    method:  'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });

  if (!upload.ok) throw new Error(`Drive-Upload fehlgeschlagen: ${await upload.text()}`);

  await saveSetting(KEY_LAST_BACKUP, new Date().toISOString());
}

async function uploadFileToDrive(
  token:    string,
  folderId: string,
  fileName: string,
  blob:     Blob,
): Promise<void> {
  // Alte Version mit gleichem Namen löschen (nur 1 Version behalten)
  const q    = encodeURIComponent(`'${folderId}' in parents and name='${fileName}' and trashed=false`);
  const list = await driveApi(`/files?q=${q}&fields=files(id)`, {}, token);
  if (list.ok) {
    const data = await list.json() as { files: Array<{ id: string }> };
    for (const f of data.files) {
      await driveApi(`/files/${f.id}`, { method: 'DELETE' }, token);
    }
  }

  const metadata = JSON.stringify({ name: fileName, parents: [folderId] });
  const body     = new FormData();
  body.append('metadata', new Blob([metadata], { type: 'application/json' }));
  body.append('file', blob, fileName);

  const upload = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
    method:  'POST',
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  if (!upload.ok) throw new Error(`Drive-Upload (${fileName}) fehlgeschlagen: ${await upload.text()}`);
}

export async function uploadHandbuchToDrive(): Promise<void> {
  const token    = await getAccessToken();
  if (token === null) throw new Error('Nicht mit Google Drive verbunden.');
  const folderId = await getOrCreateFolder(token);

  const [mdResp, pdfResp] = await Promise.all([
    fetch('/HANDBUCH.md'),
    fetch('/HANDBUCH.pdf'),
  ]);

  if (!mdResp.ok || !pdfResp.ok) throw new Error('Handbuch-Dateien nicht gefunden.');

  const [mdBlob, pdfBlob] = await Promise.all([
    mdResp.blob(),
    pdfResp.blob(),
  ]);

  await Promise.all([
    uploadFileToDrive(token, folderId, 'AMed-App-Handbuch.md',  new Blob([mdBlob],  { type: 'text/markdown' })),
    uploadFileToDrive(token, folderId, 'AMed-App-Handbuch.pdf', new Blob([pdfBlob], { type: 'application/pdf' })),
  ]);
}

export async function downloadLatestDriveBackup(): Promise<string> {
  const token    = await getAccessToken();
  if (token === null) throw new Error('Nicht mit Google Drive verbunden.');
  const folderId = await getOrCreateFolder(token);

  const q    = encodeURIComponent(`'${folderId}' in parents and name contains '.amedBackup' and trashed=false`);
  const list = await driveApi(
    `/files?q=${q}&orderBy=createdTime+desc&fields=files(id,name,createdTime)&pageSize=1`,
    {}, token,
  );
  if (!list.ok) throw new Error('Drive-Dateiliste konnte nicht geladen werden.');

  const data = await list.json() as { files: Array<{ id: string; name: string }> };
  if (data.files.length === 0) throw new Error('Kein Backup in Google Drive gefunden.');

  const file     = data.files[0]!;
  const download = await fetch(
    `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!download.ok) throw new Error(`Download fehlgeschlagen: ${download.status}`);

  return download.text();
}

export async function checkDriveNewerBackup(): Promise<string | null> {
  if (!isDriveConnected()) return null;
  try {
    const token    = await getAccessToken();
    if (token === null) return null;
    const folderId = await getOrCreateFolder(token);
    const q    = encodeURIComponent(`'${folderId}' in parents and name contains '.amedBackup' and trashed=false`);
    const list = await driveApi(
      `/files?q=${q}&orderBy=createdTime+desc&fields=files(id,createdTime)&pageSize=1`,
      {}, token,
    );
    if (!list.ok) return null;
    const data = await list.json() as { files: Array<{ id: string; createdTime: string }> };
    if (data.files.length === 0) return null;

    const driveTime = new Date(data.files[0]!.createdTime).getTime();
    const localLast = await getLastBackupTime();
    if (localLast === null) return data.files[0]!.createdTime;

    const localTime  = new Date(localLast).getTime();
    const THRESHOLD  = 10 * 60 * 1000; // 10 Minuten Toleranz für Clock-Skew
    return driveTime > localTime + THRESHOLD ? data.files[0]!.createdTime : null;
  } catch { return null; }
}

export async function autoBackupIfNeeded(): Promise<void> {
  if (!isDriveConnected()) return;

  const last = await getLastBackupTime();
  if (last !== null) {
    const hoursAgo = (Date.now() - new Date(last).getTime()) / 3_600_000;
    if (hoursAgo < BACKUP_INTERVAL_H) return;
  }

  try { await uploadDriveBackup(); } catch { /* stilles Fehlschlagen */ }
}
